import React, { useState, useCallback } from "react";
import { useFormContext } from "./FormContext";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { useRouter } from "next/router";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  sendOTPForTermsheet,
  verifyOTPForTermsheet,
} from "@/lib/credlixBypass";
import { useOtpVerification } from "@/component/useOtpVerification";
import OtpPopup from "./OtpPopup";
import { ENDPOINTS } from "@/config/endpoints";
import buyerGeneratePDF from "./buyerGeneratePDF";

const schema = () => {
  return yup.object({
    Name: yup.string().required("This field is required."),
    Company: yup.string().required("This field is required."),
    GSTNumber: yup
      .string()
      .required("This field is required.")
      .matches(
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
        "Invalid GST number."
      ),
    Email: yup
      .string()
      .required("This field is required.")
      .email("Email is not valid"),
  });
};

const Personaldetails = ({ showPrevStep }) => {
  const router = useRouter();
  const { state, dispatch } = useFormContext();
  const { formData } = state;
  const validationSchema = schema();
  const [ThankyouRes, setThankyouRes] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [errMSG, setErrMSG] = useState(false);
  const [verifyError, setVerifyError] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [editPhone, setEditPhone] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfRes, setPdfRes] = useState(null); 
  const [apiServerError, setApiServerError] = useState("")
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm({
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const {
    loader,
    phoneNumber,
    otpBtnDisabled,
    otpRequested,
    showOtpField,
    countdown,
    phoneError,
    otpInvalidFormat,
    networkError,
    verified,
    otpNumericIncorrect,
    setPhoneNumber,
    setVerified,
    handlePhoneInput,
    sendOTP,
    handleOtpChange,
    handleOtpBackspace,
  } = useOtpVerification({
    sendOTPFunc: sendOTPForTermsheet,
    verifyOTPFunc: verifyOTPForTermsheet,
  });

  const handlePrev = () => {
    showPrevStep(true);
    if (state.step == 2) {
      showPrevStep(true);
    }
    dispatch({ type: "PREV_STEP" });
  };
  const handleVerifyClick = async () => {
    setVerifyError(false);
    await sendOTP();
    setShowOtpModal(true);
  };

  const handleCaptchaSubmit = useCallback(
    (values) => {
      if (!executeRecaptcha) {
        return false;
      }
      executeRecaptcha().then((gReCaptchaToken) => {
        onFormSubmit({ ...values, phoneNumber, verified }, gReCaptchaToken);
      });
    },
    [executeRecaptcha, phoneNumber, verified]
  );
  const isMobile = typeof window !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
 
  async function onFormSubmit(values, gReCaptchaToken) {
    clearErrors();
    setErrMSG(false);
    setVerifyError(false);
    setIsSubmitting(true);
    if (values.verified === true) {
      const body = {
        formType: "BuyerEligibility",
        exporterName: values.Name,
        spoc1Email: values.Email,
        companyType: values.Company,
        exporterGstNo: values.GSTNumber,
        spoc1PhoneNumber: values.phoneNumber,
        sourceUrl: window.location.href,
        buyerDataList: [
          { buyerName: formData.buyerName.trim(), buyerCountry: formData.country },
        ],
      };
      let config = {
        method: "post",
        url: ENDPOINTS.BUYER_ELIGIBILITY_API,
        headers: {
          "Content-Type": "application/json",
          "g-recaptcha-response": gReCaptchaToken,
        },
        data: JSON.stringify(body),
      };
      try {
        const response = await axios(config);
        const resStatus = response?.data?.status;
        if (response.status == 200 && resStatus == 200) {
          const htmlContent = buyerGeneratePDF(response.data.result);
          const pdfRes = await fetch(ENDPOINTS.PDF_GENERATE_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ html: htmlContent }),
          });
          if (!pdfRes.ok) throw new Error("PDF generation failed");
          const blob = await pdfRes.blob();
          const url = URL.createObjectURL(blob);
          if (isMobile) {
            window.open(url, "_blank", "noopener,noreferrer");
          } else {
            setPdfRes({ ok: true, url });
          }
          setThankyouRes(true);
          gtag('event', 'buyer_Eligibility', {
            'event_category': 'Submit button',
            'event_label': 'Submit button'
          });
          setTimeout(() => {
            setThankyouRes(false);
          }, 3000);
          reset();
          setPhoneNumber("");
          setVerified(false);
          reset();
          state.formData == {};
         }else if (resStatus === 400 || resStatus === 500) {
          const errorMsg =
            response?.data?.data?.message ||
            response?.data?.message || response?.data?.data?.error
          setApiServerError(errorMsg);
        }
      } catch (err) {
        setErrMSG(true);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setVerifyError(true);
      setIsSubmitting(false);
    }
  }
  
  return (
    <div className="mx-auto py-4 px-3 sm:px-2 bg-white rounded-[20px] shadow-xl border border-bordercolor">
      <h2 className="text-textxl sm:text-base2 font-medium mb-xsp text-center text-[#2C2C2C]">
        Check Your Buyer Credit Worthiness
      </h2>
      <p className="text-gray-500 text-center text-base2 sm:text-sm mb-4">
        Start Export Factoring with Credlix
      </p>

      <form onSubmit={handleSubmit(handleCaptchaSubmit)}>
        <div>
          <label className="block text-sm font-medium">Full Name*</label>
          <input
            {...register("Name")}
            type="text"
            name="Name"
            id="Name"
            placeholder="Enter Your Name"
            className="block w-full mt-1 text-base flex-1 pl-2 h-6 rounded-md border-0 py-1.5 ring-1 ring-inset ring-bordercolor focus:ring-[#555] focus:outline-none"
          />
          <p className="text-progressColor4 text-xs mt-0.5">
            {errors.Name?.message}
          </p>
        </div>

        <div className="flex gap-2 mt-2 sm:flex-col">
          <div className="w-1/2 sm:w-full">
            <label className="block text-sm font-medium">Email*</label>
            <input
              {...register("Email")}
              type="text"
              placeholder="Enter email ID"
              className="block mt-1 text-base w-full flex-1 pl-2 h-6 rounded-md border-0 py-1.5 ring-1 ring-inset ring-bordercolor focus:ring-[#555] focus:outline-none"
            />
            <p className="text-progressColor4 text-xs mt-0.5">
              {errors.Email?.message}
            </p>
          </div>

          <div className="w-1/2 sm:w-full">
            <label className="block text-sm font-medium">Company*</label>
            <input
              {...register("Company")}
              type="text"
              placeholder="Enter Company Name"
              className="block mt-1 w-full text-base flex-1 pl-2 h-6 rounded-md border-0 py-1.5 ring-1 ring-inset ring-bordercolor focus:ring-[#555] focus:outline-none"
            />
            <p className="text-progressColor4 text-xs mt-0.5">
              {errors.Company?.message}
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-2 sm:flex-col">
          {/* Phone No Start */}
          <div className="w-1/2 sm:w-full">
            <label className="block text-sm font-medium">Phone No*</label>
            <div className="flex relative mt-1">
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => handlePhoneInput(e.target.value)}
                maxLength={10}
                placeholder="Enter Phone No."
                className="block w-full text-base pl-1 h-6 rounded-md border-0 py-1.5 ring-1 ring-inset ring-bordercolor focus:border-[#555] focus:ring-[#555] focus:outline-none"
              />
              {verified ? (
                <div className="absolute flex items-center right-0 z-10 bg-[#4A9447] px-1 text-xs text-white h-8 rounded-[4px] my-[4px] mr-xsp">
                  Verified
                </div>
              ) : (
                <button
                  type="button"
                  disabled={otpBtnDisabled || loader}
                  onClick={handleVerifyClick}
                  className={`absolute right-0 top-0 bottom-0 rounded-r-md text-sm px-1 text-center ${
                    otpBtnDisabled || loader
                      ? "bg-[#ddd] text-[#888] cursor-not-allowed rounded-[4px] my-[4px] mr-xsp h-8"
                      : "border border-[#086AC9] text-[#086AC9] rounded-[4px] my-[4px] mr-xsp h-8"
                  }`}
                >
                  {loader ? "Verifying..." : "Verify"}
                </button>
              )}
            </div>
            {phoneError && (
              <p className="text-progressColor4 text-xs mt-0.5">
                Invalid phone number
              </p>
            )}
            {networkError && (
              <p className="text-progressColor4 text-xs mt-0.5">
                Captcha or network issue
              </p>
            )}
            {verifyError && (
              <p className="text-progressColor4 text-xs mt-0.5">
                Phone number not verified
              </p>
            )}
            <OtpPopup
              visible={showOtpModal}
              onClose={() => setShowOtpModal(false)}
              phoneNumber={phoneNumber}
              onEditPhone={() => {
                setShowOtpModal(false);
                setEditPhone(true);
              }}
              sendOTP={sendOTP}
              otpRequested={otpRequested}
              verified={verified}
              showOtpField={showOtpField}
              countdown={countdown}
              otpInvalidFormat={otpInvalidFormat}
              otpNumericIncorrect={otpNumericIncorrect}
              handleOtpChange={handleOtpChange}
              handleOtpBackspace={handleOtpBackspace}
            />
          </div>
          {/* Phone No End  */}

          <div className="w-1/2 sm:w-full">
            <label className="block text-sm font-medium">GST Number*</label>
            <input
              {...register("GSTNumber")}
              type="text"
              placeholder="Enter GST number"
              className="block mt-1 w-full text-base flex-1 pl-2 h-6 rounded-md border-0 py-1.5 ring-1 ring-inset ring-bordercolor focus:ring-[#555] focus:outline-none"
            />
            <p className="text-progressColor4 text-xs mt-0.5">
              {errors.GSTNumber?.message}
            </p>
          </div>
        </div>
        <div className="flex gap-x-2 sm:gap-xsp items-center mt-5">
          <button
            className="flex gap-x-1 sm:gap-xsp justify-center items-center py-2 w-1/2 text-center rounded-md text-brand text-base font-medium sm:text-sm"
            onClick={handlePrev}
          >
            <svg
              xmlns="https://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="white"
              width="24"
              height="24"
              className="fill-current text-brand"
            >
              <path fill="none" d="M0 0h24v24H0z"></path>
              <path d="M7.82843 10.9999H20V12.9999H7.82843L13.1924 18.3638L11.7782 19.778L4 11.9999L11.7782 4.22168L13.1924 5.63589L7.82843 10.9999Z"></path>
            </svg>
            Buyer Eligibility
          </button>

          <button className="flex gap-x-1 sm:gap-0.5 justify-center items-center py-2 w-1/2 text-center rounded-md text-white text-base font-medium bg-gradient-to-r from-[#022B5C] to-[#0363C4] shadow-lg transition-all duration-300 hover:from-[#0363C4] hover:to-[#022B5C] sm:text-sm">
            {isSubmitting ? (
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="https://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            ) : (
              <>
                Check Eligibility
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="brand"
                  width="24"
                  height="24"
                  className="fill-current text-white"
                >
                  <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"></path>
                </svg>
              </>
            )}
          </button>
        </div>
      </form>
      {isSubmitting && <p className="text-center text-brand text-base font-medium mt-1">Processing, please wait...</p>}
      {(apiServerError || errMSG) && (
       <p className="text-sm font-medium mt-1 text-red-500">
      {apiServerError || "There is some server response error"}
      </p>)}

      {ThankyouRes && (
        <div className="text-green text-center">
          <p className="text-sm font-medium mt-3">
            Successfull Submission : Thank you for your interest.
          </p>
        </div>
      )}
   {pdfRes && pdfRes.ok && (
  <div className="fixed top-0 right-0 h-full w-2/4 sm:hidden md:w-[600px] bg-white shadow-lg z-[9999] transform transition-transform duration-300">
    <div className="flex justify-between items-center p-1 border-b">
      <h2 className="text-lg font-semibold">PDF Preview</h2>
      <button
        onClick={() => setPdfRes(null)}
        className="text-gray-500 text-xl mr-1"
      >
        ✕
      </button>
    </div>
    <iframe
      src={pdfRes.url}
      className="w-full h-[calc(100%-50px)]"
      title="Buyer Report"
    />
  </div>
)}
    </div>
  );
};

export default Personaldetails;
